import { Component, inject, PLATFORM_ID, AfterViewInit, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { SimilarTvSerieService } from '../../../../core/services/tv/similar.sevice';
import { LanguageService } from '../../../../core/services/language/language.service';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { trigger, transition, style, animate } from '@angular/animations';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CardSkeleton } from '../../../../layout/designs/card-skeleton/card-skeleton';
import { MediaCardComponent } from '../../../../shared/components/media-card/media-card.component';

@Component({
  selector: 'app-tv-similar',
  imports: [CommonModule, RouterModule, CardSkeleton, MediaCardComponent],
  templateUrl: './tv-similar.html',
  styleUrl: './tv-similar.scss',
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('500ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ]
})
export class TvSimilar implements OnInit, AfterViewInit, OnDestroy {

  public readonly languageService = inject(LanguageService);
  private similerTvSeriesService = inject(SimilarTvSerieService);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);
  private breakpointObserver = inject(BreakpointObserver);
  private platformId = inject(PLATFORM_ID);
  private destroy$ = new Subject<void>();

  @ViewChild('movieSection') movieSection!: ElementRef;

  isMobile: boolean = false;
  canScrollLeft = false;
  canScrollRight = false;
  tvId: number | null = null;
  tvName: string | null = null;
  similarTvSeriesData: any[] = [];
  isLoading = true;
  readonly initialSkeletons = Array(12).fill(0);

  ngOnInit(): void {
    this.breakpointObserver
      .observe([Breakpoints.Handset])
      .subscribe(result => {
        this.isMobile = result.matches;
      });
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const slug = params.get('slug');
      this.getSimilerTv(slug);
    });
    this.checkScrollPosition();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => this.checkScrollPosition(), 400);
    }
  }

  getSimilerTv(slugParam?: string | null) {
    const slug = slugParam !== undefined ? slugParam : this.route.snapshot.paramMap.get('slug');
    const [id, name] = slug ? slug?.split('-') : [null];
    this.tvId = id ? parseInt(id, 10) : null;
    this.tvName = name ? decodeURIComponent(name.replace(/-/g, ' ')) : null;

    if (this.tvId) {
      this.isLoading = true;
      this.similerTvSeriesService.getSimilarTvSeries(this.tvId).subscribe({
        next: (simlarTvSeries) => {
          this.similarTvSeriesData = simlarTvSeries?.results || [];
          this.isLoading = false;
          setTimeout(() => this.checkScrollPosition(), 300);
        },
        error: () => {
          this.isLoading = false;
        }
      });
    }
  }

  scrollLeft() {
    if (!this.movieSection?.nativeElement) return;
    const section = this.movieSection.nativeElement;
    const cardWidth = section.querySelector('.trending-movie-container')?.offsetWidth || 200;
    section.scrollBy({ left: -(cardWidth + 16), behavior: 'smooth' });
    setTimeout(() => this.checkScrollPosition(), 300);
  }

  scrollRight() {
    if (!this.movieSection?.nativeElement) return;
    const section = this.movieSection.nativeElement;
    const cardWidth = section.querySelector('.trending-movie-container')?.offsetWidth || 200;
    section.scrollBy({ left: cardWidth + 16, behavior: 'smooth' });
    setTimeout(() => this.checkScrollPosition(), 300);
  }

  checkScroll() {
    this.checkScrollPosition();
  }

  checkScrollPosition() {
    if (!this.movieSection?.nativeElement) return;

    const section = this.movieSection.nativeElement;
    const scrollLeft = section.scrollLeft;
    const scrollWidth = section.scrollWidth;
    const clientWidth = section.clientWidth;

    this.canScrollLeft = scrollLeft > 5;
    this.canScrollRight = scrollLeft < (scrollWidth - clientWidth - 5);

    this.cdr.detectChanges();
  }
}
